import { useEffect, useState } from 'react'

import { navigate, useLocation, useParams } from '@redwoodjs/router'

import { useAuth } from 'src/auth'
import { ActivationCodeFormType } from 'src/components/features/Auth/EnterActivationCodeForm/EnterActivationCodeForm'
import { RegistrationFormType } from 'src/lib/hooks/auth/useUserRegistration'
import useResetUserCode from 'src/lib/hooks/mutations/useResetUserCode'

import { useImportViews } from '../mutations/recentlyViewed/useImportViews'

import { useCurrentUser } from './useCurrentUser'

type UseActivationCodeProp = {
  backRoute?: (
    params: { email: string } & Record<string, string | number | boolean>
  ) => string
  nextRoute?: (params: Record<string, string | number | boolean>) => string
}

export function useActivationCode({
  backRoute,
  nextRoute,
}: UseActivationCodeProp = {}) {
  const [error, setError] = useState('')
  const { logIn, loading } = useAuth()
  const { email } = useCurrentUser()
  const { pathname, search } = useLocation()
  const [resetUserCode] = useResetUserCode()
  const importViews = useImportViews()
  const { isFirstLogin: isFirstLoginStr, ...params } = useParams()
  const isFirstLogin = isFirstLoginStr !== 'false'

  useEffect(() => {
    if (nextRoute && email) {
      navigate(nextRoute(params))
    }
  }, [email, nextRoute, params])

  const sendActivationCode = async (data: ActivationCodeFormType) => {
    const response = await logIn({
      username: data.email,
      password: data.activationCode,
    })
    if (response.error) {
      setError(response.error)
    } else {
      importViews()
    }
    return response
  }

  const onChange = (data: RegistrationFormType) => {
    if (backRoute) {
      navigate(
        backRoute({
          ...params,
          email: encodeURIComponent(data.email.toLowerCase()),
        })
      )
    }
  }

  const onResendCode = async (email: string) => {
    await resetUserCode({
      variables: {
        input: {
          email,
          activateUserRoute: `${pathname}${search}`,
        },
      },
    })
  }

  return {
    error,
    loading,
    sendActivationCode,
    onChange,
    onResendCode,
    isFirstLogin,
  }
}
